import React from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Button } from '@Comp';
import { FaUpload, FaForward, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import DocumentUploader from '../../business-profile-copy/dropzone';
import onboardingStore from '../../../stores/onboardingStore';
import './style.scss';

@inject('commonStore', 'businessProfileStore')
@withRouter
@observer
export default class OnboardingStep4 extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            uploadMethod: onboardingStore.pastInvoices.uploadMethod || null, // 'upload' | 'skip'
            uploadedFiles: onboardingStore.pastInvoices.files || [],
            errorMessage: '',
            uploadComplete: false
        };
    }

    componentDidMount() {
        // Restore previous state if exists
        if (onboardingStore.pastInvoices.uploadMethod === 'upload' && onboardingStore.pastInvoices.files.length > 0) {
            this.setState({
                uploadMethod: 'upload',
                uploadedFiles: onboardingStore.pastInvoices.files,
                uploadComplete: true
            });
        } else if (onboardingStore.pastInvoices.uploadMethod === 'skip') {
            this.setState({
                uploadMethod: 'skip'
            });
        }
    }

    handleMethodChange = (method) => {
        this.setState({
            uploadMethod: method,
            errorMessage: '',
            uploadedFiles: [],
            uploadComplete: false
        });
        onboardingStore.setPastInvoiceMethod(method);

        if (method === 'skip') {
            onboardingStore.skipPastInvoices();
        }
    };

    handleFileUploadComplete = () => {
        // This callback is triggered after DocumentUploader successfully uploads files
        // We need to mark the upload as complete
        this.setState({
            uploadComplete: true,
            errorMessage: ''
        });
    };

    handleFileStatusChange = (filesInfo) => {
        // Track uploaded files from DocumentUploader
        if (filesInfo && filesInfo.length > 0) {
            const fileList = filesInfo.map(f => ({
                name: f.meta.name,
                size: f.meta.size,
                id: f.meta.id
            }));

            this.setState({
                uploadedFiles: fileList
            });

            onboardingStore.setPastInvoiceFiles(fileList);
        }
    };

    handleNext = () => {
        const { uploadMethod, uploadComplete } = this.state;

        // Validation
        if (!uploadMethod) {
            this.setState({ errorMessage: 'Please select an option to continue' });
            return;
        }

        if (uploadMethod === 'upload' && !uploadComplete) {
            this.setState({ errorMessage: 'Please upload at least one invoice or skip this step' });
            return;
        }

        // Mark step complete
        onboardingStore.completeStep4();

        // Navigate to step 5
        this.props.history.push('/home/onboarding/step5');
    };

    handleBack = () => {
        this.props.history.push('/home/onboarding/step3');
    };

    isNextEnabled = () => {
        const { uploadMethod, uploadComplete } = this.state;

        if (uploadMethod === 'upload') {
            return uploadComplete;
        }

        if (uploadMethod === 'skip') {
            return true;
        }

        return false;
    };

    renderUploadedFilesList = () => {
        const { uploadedFiles } = this.state;

        if (!uploadedFiles || uploadedFiles.length === 0) {
            return null;
        }

        return (
            <div className="uploaded-files-list">
                <h5>Uploaded Files ({uploadedFiles.length})</h5>
                <ul>
                    {uploadedFiles.map((file, idx) => (
                        <li key={idx}>
                            <FaCheckCircle className="success-icon-small" />
                            <span className="file-name">{file.name}</span>
                            {file.size && (
                                <span className="file-size">
                                    ({(file.size / 1024 / 1024).toFixed(2)} MB)
                                </span>
                            )}
                        </li>
                    ))}
                </ul>
            </div>
        );
    };

    render() {
        const { uploadMethod, errorMessage, uploadComplete } = this.state;
        const { commonStore, businessProfileStore } = this.props;
        const profileId = commonStore?.businessProfile?.id;

        // File upload configuration for DocumentUploader
        const fileInfo = {
            files: [],
            path: '/public/upload',
            refId: profileId || 'onboarding-temp',
            ref: 'pastinvoice-onboarding',
            source: 'content-manager',
            field: 'invoiceCopy'
        };

        return (
            <div className="step-content">
                <h3 className="step-title">Upload Past Invoices</h3>
                <p className="step-description">
                    Uploading past invoices helps improve your credit score by showing you have corporate debtors with outstanding invoices.
                </p>

                {errorMessage && (
                    <div className="error-message">
                        {errorMessage}
                    </div>
                )}

                {/* Upload Method Options */}
                <div className="link-options">
                    {/* Option 1: Upload Invoices */}
                    <div className={`link-option ${uploadMethod === 'upload' ? 'active' : ''}`}>
                        <label className="radio-label">
                            <input
                                type="radio"
                                name="uploadMethod"
                                value="upload"
                                checked={uploadMethod === 'upload'}
                                onChange={() => this.handleMethodChange('upload')}
                            />
                            <span className="radio-text">
                                <FaUpload className="option-icon" />
                                Upload Invoices
                            </span>
                        </label>

                        {uploadMethod === 'upload' && (
                            <div className="method-content">
                                <div className="upload-container">
                                    {profileId ? (
                                        <>
                                            <DocumentUploader
                                                fileData={fileInfo}
                                                maxFiles={10}
                                                multiple={true}
                                                inputContent="Drop invoice PDF files here or click to browse (Max 10 files, 10MB each)"
                                                doNotDisable={true}
                                                callback={this.handleFileUploadComplete}
                                            />
                                            {uploadComplete && (
                                                <div className="upload-success">
                                                    <FaCheckCircle className="success-icon" />
                                                    <span>Files uploaded successfully</span>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div className="upload-info-message">
                                            <p>Please complete previous steps to enable file upload.</p>
                                        </div>
                                    )}
                                </div>
                                <div className="upload-guidelines">
                                    <p><strong>Guidelines:</strong></p>
                                    <ul>
                                        <li>Only PDF files are accepted</li>
                                        <li>Maximum 10 files</li>
                                        <li>Each file must be under 10MB</li>
                                        <li>Invoices should show outstanding amounts from corporate clients</li>
                                    </ul>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Option 2: Skip This Step */}
                    <div className={`link-option ${uploadMethod === 'skip' ? 'active' : ''}`}>
                        <label className="radio-label">
                            <input
                                type="radio"
                                name="uploadMethod"
                                value="skip"
                                checked={uploadMethod === 'skip'}
                                onChange={() => this.handleMethodChange('skip')}
                            />
                            <span className="radio-text">
                                <FaForward className="option-icon" />
                                Skip this step for now
                            </span>
                        </label>

                        {uploadMethod === 'skip' && (
                            <div className="method-content">
                                <div className="skip-info">
                                    <p>You can upload invoices later from your dashboard.</p>
                                    <p className="skip-note">
                                        Note: Uploading past invoices can improve your credit assessment and may result in better loan terms.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="action-buttons">
                    <Button
                        color="secondary"
                        outline
                        onClick={this.handleBack}
                    >
                        Back
                    </Button>
                    <Button
                        color="primary"
                        onClick={this.handleNext}
                        disabled={!this.isNextEnabled()}
                    >
                        Next
                    </Button>
                </div>
            </div>
        );
    }
}
